import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Alert, CircularProgress, Paper, Stack, Typography } from "@mui/material";

import { AlertPanelBasic } from "../../alert-panel/AlertPanelBasic";
import { FormikTextField } from "../../formik/FormikTextField";
import { AccordionKeys } from "../../utils/accordion-keys/AccordionKeys";
import { ClientContent } from "../client-content/ClientContent";
import { ClientTabBase } from "../client-tab-base/ClientTabBase";
import { ClientNoteMaxLength } from "./ClientNoteView.types";
import { useClientNote } from "./hooks/useClientNote";

const NOTE_ROWS = 8;

interface Props {
  clientId: string;
  /** Names who else sees the note — the other party differs per app (client vs. counselor). */
  cardTitle: string;
  noticeText: string;
  navigation?: ReactNode;
  navigationFooter?: ReactNode;
}

export function ClientNoteView({ clientId, cardTitle, noticeText, navigation, navigationFooter }: Props) {
  const { t } = useTranslation();
  const { formik, isPending, isError } = useClientNote(clientId);

  const renderContent = () => {
    if (isError) {
      return <Alert severity="error">{t("clients.client.clientNote.loadError")}</Alert>;
    }
    if (isPending) {
      return <CircularProgress aria-label={t("clients.client.clientNote.loading")} />;
    }
    return (
      <ClientContent formik={formik}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack gap={2}>
            <Typography variant="h5" component="h2">
              {cardTitle}
            </Typography>
            <FormikTextField
              formik={formik}
              fieldName="note"
              label={t("clients.client.clientNote.note")}
              multiline
              rows={NOTE_ROWS}
              slotProps={{ input: { inputProps: { maxLength: ClientNoteMaxLength.note } } }}
            />
          </Stack>
        </Paper>
      </ClientContent>
    );
  };

  return (
    <Stack flex={1} gap={2} overflow={"hidden"}>
      <AlertPanelBasic
        severity="error"
        title={t("clients.client.clientNote.noticeTitle")}
        storageKey={AccordionKeys.notice.clientNote}
      >
        <Typography variant="body2">{noticeText}</Typography>
      </AlertPanelBasic>
      <ClientTabBase navigation={navigation} navigationFooter={navigationFooter}>
        {renderContent()}
      </ClientTabBase>
    </Stack>
  );
}
